import axios from 'axios'
import { toApiError, ERROR_CODES } from './apiError'
import { endpoints } from './endpoints'
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  handleUnauthorized,
  setAccessToken,
  setRefreshToken,
} from './authToken'
import { mockAdapter } from '@/mocks/mockAdapter'
import { config } from './config'

/**
 * The one HTTP client. Screens never import axios; they call service functions
 * that call this instance.
 *
 * ── Hybrid transport ────────────────────────────────────────────────────
 * Authentication is live and nothing else is, so the adapter chooses per
 * request: auth paths go to the network, everything else is answered from
 * fixtures.
 *
 * It is one instance rather than two on purpose. Interceptors, header injection,
 * envelope unwrapping, ApiError normalisation, the refresh queue and timeouts
 * are then provably identical across both halves — so as each real endpoint
 * lands, moving it over is one entry in LIVE_PATHS and nothing else.
 */

/**
 * Paths the real API serves today. Everything else falls through to fixtures.
 *
 * Prefix-matched, so `/v1/assets/classes/{id}` is covered by its collection.
 *
 * ── The `/v1/assets` namespace is SPLIT, and the order of these matters ──
 * Five hierarchy collections, the tree and the class library are live under
 * `/v1/assets/*`. `endpoints.assets` — the demo asset register at `/v1/assets`
 * itself — is NOT, and must keep falling through to fixtures. It does, because
 * nothing here is a prefix of `/v1/assets/{id}`: every live entry names its own
 * sub-resource. Never add a bare `/v1/assets` to this list; it would swallow the
 * demo register whole.
 */
const LIVE_PATHS = [
  /* Authentication. All four now sit under `/v1/auth`, so one prefix would
     cover them — they stay listed individually because this array is also the
     record of WHICH endpoints are real, and collapsing four known routes into
     a namespace would quietly claim anything later added under it. */
  endpoints.auth.login,
  endpoints.auth.me,
  endpoints.auth.refresh,
  endpoints.auth.forgotPassword,
  /* Files. The resource prefix, which covers both API ends of the upload
     handshake and the view-url read. The presigned PUT in the middle never
     comes through here at all — it is a bare `fetch` to object storage,
     deliberately outside this instance. */
  endpoints.files.root,
  /* The WHOLE `/v1/users` namespace — the directory, one person, the profile
     record, the roles, the permission catalogue and the two public token routes.
     One prefix, where this used to be three individual entries.

     Those three were individual for a stated reason: a bare `/v1/users` would
     have swallowed the demo directory at `/v1/users` itself, which was a
     different resource still on fixtures. That is no longer true — the directory
     is live, and it is the same resource rather than a neighbour of one, so
     there is nothing left under this prefix to protect. Unlike `/v1/assets`,
     which stays split for exactly the reason this one no longer needs to be.

     It claims `/v1/users/import` and `/v1/users/imports` too, which the API does
     not serve. That is intended: bulk import is demo-only, nothing in `src/`
     calls those paths any more, and in live mode a 404 is the honest answer — a
     fixture answering there would report having created accounts that do not
     exist. `endpoints.people` keeps them spelled out for mock mode. */
  endpoints.users.list,
  /* Shift PATTERNS — the templates behind Settings → Shift Master.

     This claims `/v1/shifts` whole, and that is now all it claims: the demo
     roster, the current-shift chip and the retired handover route have been
     moved out to sibling paths (`/shift-roster`, `/shift-current`) precisely
     because this prefix swallowed them.

     ── It used to claim them too, and the note here said that was fine ──────
     The argument was that a 404 in live mode is the honest answer for a demo
     surface sitting under a real resource. Two things were wrong with it. The
     answer was not a 404 — `/shifts/current` matched `/shifts/{shift_id}` and
     422'd on the UUID parse, while the roster's `?type=all` was rejected by an
     enum — and one of the three was a POST at a live collection. And the escape
     hatch it relied on, "the roster still answers under `VITE_AUTH_MODE=mock`",
     was never exercised: nothing in this repo sets that variable, and
     DEMO-MODE.md tells you not to.

     So the rule is now the stronger one: NOTHING demo-only may sit beneath a
     live prefix. `scripts/verify-paths.mjs` enforces it. */
  endpoints.shifts.patterns,
  /* The organisation record behind Settings → General, and the zone list the
     profile form offers — which is the same set its validator checks against.
     One prefix covers both. */
  endpoints.account.current,
  endpoints.assetClasses.list,
  // endpoints.hierarchy.tree,  // retired 2026-10-13 — nothing reads `/assets/hierarchy`
  endpoints.hierarchy.plants,
  endpoints.hierarchy.lines,
  endpoints.hierarchy.machines,
  /* Data Fusion. The connection resource, which the API mounts UNDER sensors at
     `/v1/sensors/connections` — the wizard creates a connection, reads the
     connector catalogue and probes an endpoint, and the collection, detail,
     update and delete routes under it are all real endpoints with no fixture
     behind them either.

     Redundant, strictly: `endpoints.sensors.list` is `/v1/sensors` and already
     covers everything beneath it. Listed anyway, because this array is the
     record of which resources are real and Data Fusion being one of them should
     not depend on a reader noticing it is nested under another. */
  endpoints.dataFusion.connections,
  /* Sensors. The whole resource — `POST`, `GET` (collection and detail), `PUT`
     and `DELETE` — plus the two master-data collections mounted under it,
     `/waveform-types` and `/value-units`. One prefix covers all of them, since
     `isLivePath` matches on the start of the path. There is no mock route under
     this prefix on purpose: a fixture here would claim to have stored a sensor
     that no broker will ever be told to read. */
  endpoints.sensors.list,
  /* Sensor charts — create, list, update, delete and the readings history.
     Redundant under `/v1/sensors` above, and listed for the same reason Data
     Fusion is: this array is the record of which resources are real. */
  endpoints.sensorCharts.list,
  /* Work orders. The whole resource — list, detail, create, update, delete —
     behind `work_orders.manage` or, for the read and the update,
     `work_orders.update_assigned`.

     Five fixture routes DO still sit under this prefix (`mockAdapter.js`, the
     `/v1/work-orders` collection and detail). They are unreachable while the
     backend is up — this entry shadows them — and they are kept for the mock
     transport rather than for the app, which reads the real collection from the
     board, the table, both dialogs and the public page's drafted-order card.
     They are allowlisted in `scripts/verify-paths.mjs` for exactly that reason.

     This note used to claim there was no fixture left here. There is; it is
     simply never consulted. */
  endpoints.workOrders.list,
  /* The log book. The whole resource — list, detail, create, update, delete —
     behind `logbook.manage` or `logbook.record_entries`.

     The fixture route under this prefix is retired, the same call made for
     sensors and work orders: a mock answering here would report an observation
     saved to a book nobody can read. `mocks/fixtures/logbook.js` itself STAYS —
     the ALR-4471 analysis quote and `fixtures/audit.js` both read `logEntries()`
     directly rather than over HTTP, and neither is live. The shift handover was
     the third reader until its route was retired. */
  endpoints.logbook.list,
  /* Documents & SOPs. The whole resource — list, detail, create, update,
     delete — behind `documents.view` for the reads and the two manage
     permissions for the writes.

     The fixture route was at `/v1/data-fusion/documents`, which this prefix does
     NOT cover and which is retired separately in `mockAdapter.js`. What stays on
     fixtures under a different path is Asset Detail's Documents tab
     (`/v1/assets/{id}/documents`) — a route the API does not serve at all, and a
     separate pass. */
  endpoints.documents.list,
  /* Anomalies. List, detail and PATCH are served. Deliberately NOT
     `endpoints.alerts.list` — that prefix stays on fixtures, because the two are
     different resources and the demo surfaces still read it. See endpoints.js. */
  endpoints.anomalies.list,
  /* Copilot. Two prefixes cover the whole resource: `sessions` reaches
     `sessions/{id}/turns`, and `turns` reaches `turns/{id}/stream`.

     Creating a session and creating a turn are ordinary JSON requests and do
     come through here. The stream does not — it is a bare `fetch` in
     `services/sse.js`, because SSE has no place in an axios response. It is
     claimed by this prefix anyway, so that a reader finding `/copilot/turns/…`
     in the network panel is not left wondering which half of the app served it.

     `copilot.messages` and `copilot.prompts` stay on fixtures: they feed the
     public page's scripted conversation, which is a different thing entirely.

     What used to be here and is now gone: `/copilot/anomalies/{id}/turns`. That
     route never existed on this server. It was listed as live, answered 404,
     and failed quietly by design — which presented as an incident nobody had
     ever asked about. Per-anomaly history is a session lookup now; see
     `features/copilot/session.js`. */
  endpoints.copilot.sessions,
  endpoints.copilot.turns,
  /* MCP consent. Listed individually, not as `/v1/mcp`, for the reason the
     auth entries are: this array is the record of which routes are real. */
  endpoints.mcp.preview,
  endpoints.mcp.decision,
]

const isLivePath = (url = '') => LIVE_PATHS.some((path) => url.startsWith(path))

/**
 * Whether to talk to the real server at all.
 *
 * This reads VITE_AUTH_MODE rather than a flag of its own because auth and the
 * live endpoints are the same FastAPI process — "is the backend running" is one
 * question, not two. Setting it to `mock` therefore takes every live path back
 * to the fixture layer, which is also the only way to open the app with no
 * server at all.
 */
const backendReachable = () => config.authMode === 'live'

/** Resolved once. Asking axios for its own default avoids guessing xhr vs fetch. */
const networkAdapter = axios.getAdapter(axios.defaults.adapter)

function hybridAdapter(cfg) {
  if (config.mode === 'live') return networkAdapter(cfg)
  if (backendReachable() && isLivePath(cfg.url)) return networkAdapter(cfg)
  return mockAdapter(cfg)
}

export const http = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: config.requestTimeoutMs,
  // Same-origin through the dev proxy, so cookies travel regardless. Enabled in
  // live mode for the day the API is served from another origin with CORS.
  withCredentials: config.mode === 'live',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  adapter: hybridAdapter,
})

/** Correlation id per request, so a support ticket can name one call. */
let seq = 0
const requestId = () => `web-${Date.now().toString(36)}-${(++seq).toString(36)}`

/* ── Request ─────────────────────────────────────────────────────────── */
http.interceptors.request.use((cfg) => {
  const token = getAccessToken()
  if (token) cfg.headers.Authorization = `Bearer ${token}`

  cfg.headers['X-Request-Id'] = requestId()

  // UI language, negotiated by the server for any server-rendered copy.
  cfg.headers['Accept-Language'] = document?.documentElement?.lang || 'en'

  // AI output language is a first-class parameter from day one so it is never
  // retrofitted. It travels separately from Accept-Language on purpose: what
  // the model writes in is an org/user setting, not the reader's browser.
  if (cfg.meta?.ai) {
    cfg.headers['X-Forge-AI-Locale'] = config.aiOutputLocale
  }

  return cfg
})

/* ── Response envelope ───────────────────────────────────────────────── */

/**
 * The API wraps every success in `{message, response_code, status, data}`.
 * Callers want the payload, so it is unwrapped once here instead of in each of
 * them — otherwise every feature reimplements the same `.data.data`.
 *
 * Shape-detected rather than path-detected, so mock responses (already
 * unwrapped) pass through untouched. All four keys must be present: a payload
 * that merely happens to carry a `data` property is not an envelope.
 */
export function unwrapEnvelope(body) {
  const looksLikeEnvelope =
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    'data' in body &&
    'status' in body &&
    'message' in body &&
    typeof body.response_code === 'number'

  return looksLikeEnvelope ? body.data : body
}

/* ── Refresh ─────────────────────────────────────────────────────────── */

/**
 * Exchange the refresh token for a new pair.
 *
 * The API returns a NEW refresh token alongside the access token without
 * invalidating the old one, so both are stored. Keeping the original would work
 * today but would break the moment the server starts rotating for real.
 *
 * `_skipAuthRefresh` marks this call so a 401 from refresh cannot recurse back
 * into here.
 */
let inFlightRefresh = null

export function refreshSession() {
  // Concurrent 401s must share one refresh, or they stampede — and with a
  // rotating token, the losers of that race would spend one already spent.
  inFlightRefresh ??= (async () => {
    const refreshToken = getRefreshToken()
    // Nothing to refresh with. Thrown rather than returned so a caller's catch is
    // the single place that handles "the session is gone".
    if (!refreshToken) throw new Error('no-refresh-token')

    const response = await http.post(
      endpoints.auth.refresh,
      { refresh_token: refreshToken },
      { _skipAuthRefresh: true },
    )
    // Already unwrapped by the response interceptor.
    const session = response.data

    if (!session?.access_token) throw new Error('refresh-returned-no-token')

    setAccessToken(session.access_token)
    if (session.refresh_token) setRefreshToken(session.refresh_token)
    return session
  })().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

/* ── Response ────────────────────────────────────────────────────────── */
http.interceptors.response.use(
  (response) => {
    response.data = unwrapEnvelope(response.data)
    return response
  },
  async (error) => {
    const apiError = toApiError(error)
    const original = error.config

    /**
     * A 401 from login is an ANSWER — the credential was wrong — not an expired
     * session. Refreshing on it would turn a failed sign-in into a token refresh
     * and silently re-POST the credential. The sign-in screen handles it.
     *
     * A 401 from refresh means the refresh token is spent or expired: nothing is
     * left to try, so it falls to the terminal branch below.
     */
    const isLoginAttempt = original?.url === endpoints.auth.login
    const isRefreshAttempt = original?.url === endpoints.auth.refresh || original?._skipAuthRefresh

    if (apiError.code === ERROR_CODES.UNAUTHORIZED && !isLoginAttempt && !isRefreshAttempt) {
      // One attempt only. Retrying against a dead session is just a slower route
      // to the same redirect.
      if (original && !original._retried) {
        original._retried = true
        try {
          await refreshSession()
          // Reissued with the new token: the request interceptor reads the cookie
          // again on the way back out.
          return http(original)
        } catch {
          // The refresh itself failed, so the session is genuinely over.
          handleUnauthorized()
        }
      } else {
        handleUnauthorized()
      }
    }

    // A refresh that 401s ends the session. The branch above deliberately skips
    // it, so the handoff happens exactly once, here.
    if (apiError.code === ERROR_CODES.UNAUTHORIZED && isRefreshAttempt) {
      clearSession()
      handleUnauthorized()
    }

    if (import.meta.env.DEV) {
      // Method and path only. Never the body — it can carry a credential.
      console.warn(
        `[api] ${apiError.code}`,
        `${original?.method?.toUpperCase() ?? '?'} ${original?.url ?? '?'}`,
        apiError.requestId ?? '',
      )
    }

    return Promise.reject(apiError)
  },
)

/** Unwrap to the payload. Every service function goes through this. */
export async function request(cfg) {
  const response = await http(cfg)
  return response.data
}

export const get = (url, cfg) => request({ ...cfg, method: 'get', url })
export const post = (url, data, cfg) => request({ ...cfg, method: 'post', url, data })
export const patch = (url, data, cfg) => request({ ...cfg, method: 'patch', url, data })
// PUT rather than PATCH is not a style choice: the API has no partial-patch
// semantics anywhere, so every update sends the whole updatable record.
export const put = (url, data, cfg) => request({ ...cfg, method: 'put', url, data })
export const del = (url, cfg) => request({ ...cfg, method: 'delete', url })
